import React, { useEffect, useState } from "react";
import { getAdminProfile, getAdminUsers } from "../api/getAdminProfile";
import { useNavigate } from "react-router-dom";
import { Admin } from "../types/admin.type";
import { Worker } from "../types/worker.type";
import { useAuth } from "../auth/useAuth";
import { UPLOAD_URL } from "../config/config";

const Profile: React.FC = () => {
  const [adminData, setAdminData] = useState<Admin | null>(null);
  const [employees, setEmployees] = useState<Worker[] | null>(null);
  const [managers, setManagers] = useState<Worker[] | null>(null);
  const { user, isLoading } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!user) {
          setError("No user data found");
          return;
        }

        const cleanId = user.id.trim();
        const data = await getAdminProfile(cleanId);

        setAdminData(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading) {
      fetchProfile();
    }
  }, [user, isLoading]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (!storedUser) {
          setError("No user data found");
          return;
        }

        const user = JSON.parse(storedUser);
        user.id = user.id.trim();

        if (adminData && adminData.type === "ADMIN") {
          const data = await getAdminUsers(user.id);
          setEmployees(data.employeeId_Employees);
          setManagers(data.managerId_Managers);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [adminData]);

  const goToUpdate = (userId: string) => {
    navigate(`/update-profile/${userId}`);
  };

  if (loading) return <p>Loading profile...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!adminData) return <p>No profile data found.</p>;

  return (
    <div style={styles.container}>
      <div style={styles.section}>
        <h2 style={styles.heading}>{adminData.name} Profile</h2>
        <img
          src={`${UPLOAD_URL}${adminData.profileImage}`}
          alt="Profile"
          style={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            objectFit: "cover",
            marginBottom: "1rem",
          }}
        />

        <p style={styles.text}>
          <strong>Name:</strong> {adminData.name} {adminData.surname}
        </p>
        <p style={styles.text}>
          <strong>Email:</strong> {adminData.email}
        </p>
        <p style={styles.text}>
          <strong>Role:</strong> {adminData.type}
        </p>
        <p style={styles.text}>
          <strong>City:</strong> {adminData.city || "N/A"}
        </p>
        <p style={styles.text}>
          <strong>Country:</strong> {adminData.country || "N/A"}
        </p>
      </div>

      {adminData?.type === "ADMIN" && (
        <div style={styles.section}>
          <button
            style={styles.button}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor =
                styles.buttonHover.backgroundColor)
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor =
                styles.button.backgroundColor)
            }
            onClick={() => goToUpdate(adminData.id)}
          >
            Update Profile
          </button>
          <h3 style={styles.heading}>Managers</h3>
          {managers?.map((user) => (
            <div key={user.id} style={styles.userCard}>
              <p style={styles.text}>
                <strong>Name:</strong> {user.id_Worker?.id_User?.name}
              </p>
              <p style={styles.text}>
                <strong>Email:</strong> {user.id_Worker?.id_User?.email}
              </p>
              <p style={styles.text}>
                <strong>Type:</strong> Manager
              </p>
              <button
                style={styles.button}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    styles.buttonHover.backgroundColor)
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    styles.button.backgroundColor)
                }
                onClick={() => goToUpdate(user.id)}
              >
                Update Profile
              </button>
            </div>
          ))}

          <h3 style={styles.heading}>Employees</h3>
          {employees?.map((user) => (
            <div key={user.id} style={styles.userCard}>
              <p style={styles.text}>
                <strong>Name:</strong> {user.id_Worker?.id_User?.name}
              </p>
              <p style={styles.text}>
                <strong>Email:</strong> {user.id_Worker?.id_User?.email}
              </p>
              <p style={styles.text}>
                <strong>Type:</strong> Employee
              </p>
              <button
                style={styles.button}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    styles.buttonHover.backgroundColor)
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    styles.button.backgroundColor)
                }
                onClick={() => goToUpdate(user.id)}
              >
                Update Profile
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "700px",
    margin: "2rem auto",
    padding: "2rem",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    fontFamily: "Arial, sans-serif",
  },
  section: {
    marginBottom: "2rem",
  },
  heading: {
    fontSize: "1.8rem",
    marginBottom: "1rem",
    color: "#333",
  },
  text: {
    fontSize: "1rem",
    marginBottom: "0.5rem",
    color: "#555",
  },
  userCard: {
    backgroundColor: "#f5f7fa",
    padding: "1rem",
    borderRadius: "10px",
    marginBottom: "1rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  button: {
    padding: "0.5rem 1rem",
    backgroundColor: "#007bff",
    border: "none",
    borderRadius: "6px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "0.9rem",
    marginTop: "0.5rem",
  },
  buttonHover: {
    backgroundColor: "#0056b3",
  },
};

export default Profile;
